import React from 'react';

import DataTable from '../../shared/data_table';
import StatusPill from '../../shared/status_pill';
import { DECISION, DECISION_LABEL } from './decisions';
import { VERDICT } from './params';

/*
 * the persisted preview, one line per file row with its verdict and every code on it (not only the
 * first), so Ops can fix the whole file in one pass. Props-only; rows are upload_row_view.
 */
const decision_cell = (r, decisions, can_decide, on_skip, on_compare) => {
  const d = decisions[r.line];
  // an applied run: what was decided, by whom and why
  if (!can_decide && r.decision_text) return r.decision_text;
  if (r.verdict === VERDICT.NEEDS_CONFIRMATION) {
    return (
      <span>
        {d ? DECISION_LABEL[d.action] : 'Needs a decision'}{' '}
        {can_decide ? (
          <button type="button" className="tp-link" onClick={() => on_compare(r.line)}>
            {d ? 'Change' : 'Compare and decide'}
          </button>
        ) : null}
      </span>
    );
  }
  if (r.verdict !== VERDICT.VALID) return '—';
  const skipped = d && d.action === DECISION.SKIP;
  if (!can_decide) return skipped ? 'Skipped' : '—';
  return (
    <span>
      {skipped ? 'Skipped ' : ''}
      <button
        type="button"
        className="tp-link"
        aria-label={skipped ? `Undo skip of row ${r.line}` : `Skip row ${r.line}`}
        onClick={() => on_skip(r.line)}
      >
        {skipped ? 'Undo' : 'Skip'}
      </button>
    </span>
  );
};

const PreviewTable = ({
  rows,
  decisions = null,
  can_decide = false,
  on_skip = () => {},
  on_compare = () => {}
}) => {
  const columns = [
    { key: 'line', label: 'Row' },
    {
      key: 'verdict',
      label: 'Result',
      render: (r) => <StatusPill tone={r.verdict_tone}>{r.verdict_label}</StatusPill>
    },
    {
      key: 'teacher_name',
      label: 'Teacher',
      render: (r) => (
        <span>
          <b>{r.teacher_name}</b>
          {r.teacher_sub ? <div className="tp-card-field-label">{r.teacher_sub}</div> : null}
        </span>
      )
    },
    { key: 'category_label', label: 'Category' },
    { key: 'amount_text', label: 'Amount', align: 'right' },
    { key: 'entry_text', label: 'Entry date' },
    {
      key: 'reference_id',
      label: 'Reference',
      render: (r) =>
        r.reference_generated ? (
          <span>
            {r.reference_id}{' '}
            <span className="tp-auto-tag" title="Generated from the row details">
              auto
            </span>
          </span>
        ) : (
          r.reference_id
        )
    },
    // The teacher sees this note on their Earnings page.
    { key: 'note', label: 'Note', render: (r) => r.note || '—' },
    {
      key: 'issues',
      label: 'Issues',
      render: (r) =>
        r.issues.length || r.lookalikes.length ? (
          <ul className="tp-dup-list">
            {/* One code can appear twice on a row (two INVALID_REQUEST fields): keys carry the index. */}
            {r.issues.map((i, n) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`issue-${n}-${i.code}`} data-code={i.code}>
                {i.text}
              </li>
            ))}
            {r.lookalikes.map((text, n) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`lookalike-${n}`}>{text}</li>
            ))}
          </ul>
        ) : (
          '—'
        )
    },
    // shown while decisions are open (or were recorded on an applied run)
    ...(decisions
      ? [
          {
            key: 'decision',
            label: 'Decision',
            render: (r) => decision_cell(r, decisions, can_decide, on_skip, on_compare)
          }
        ]
      : [])
  ];
  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      cardTitle={(r) => (
        <span>
          Row {r.line} · <StatusPill tone={r.verdict_tone}>{r.verdict_label}</StatusPill>
        </span>
      )}
      emptyText="No rows match this filter."
    />
  );
};

export default PreviewTable;
